import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ShortCourseOrderModel } from 'src/app/core/ShortCourseOrderModel';
import { AdminService } from 'src/app/services/admin/admin.service';
import { OrderService } from 'src/app/services/order/order.service';
import { XCourseFilter } from 'src/app/core/common/XCourseFilter';

@Component({
  selector: 'app-x-single-enquire',
  templateUrl: './x-single-enquire.component.html',
  styleUrl: './x-single-enquire.component.scss'
})
export class XSingleEnquireComponent implements OnInit, OnDestroy {
  // Store selected course details
  selectedCourseDetails: any = null;

  // Store selected parent UOC details
  selectedParentUOCDetails: any = null;

  // Control course modal visibility
  showCourseModal = false;


  // Store selected course type
  selectedCourseType: 'UOC' | 'VOC' = 'UOC';

  // Store selected course ID
  selectedCourseId: number | null = null;

  // Store VOC ID from route
  selectedVOCIdFromRoute: number | null = null;

  // Track route course opening
  openCourseFromRoute = false;

  // Track direct enquiry mode
  isDirectEnquiry = true;

  // Track route params loaded
  private routeParamsLoaded = false;

  // Track UOC courses loaded
  private uocCoursesLoaded = false;

  // Track VOC courses loaded
  private vocCoursesLoaded = false;

  // Track initial course applied
  private initialCourseApplied = false;


  // Store all UOC courses
  allUOCCourses: any[] = [];

  // ============================================================
  // ADDITIONAL UOC DROPDOWN - SQL PAGINATION (50 PER REQUEST)
  // ============================================================
  dropdownUOCCourses: any[] = [];
  dropdownPageSize = 50;
  dropdownTotalRecords = 0;
  dropdownSearchText = '';
  dropdownLoading = false;
  dropdownHasLoaded = false;

  private loadedDropdownPages = new Set<number>();
  private dropdownRequestVersion = 0;
  private dropdownSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private dropdownScrollElement: HTMLElement | null = null;
  private dropdownScrollHandler = () => {
    const el = this.dropdownScrollElement;
    if (!el || el.scrollTop + el.clientHeight < el.scrollHeight - 35) {
      return;
    }
    this.onCourseDropdownScroll();
  };

  // Store all VOC courses
  allVOCCourses: any[] = [];

  // Store selected additional UOCs
  selectedAdditionalUOCs: any[] = [];

  // Store selected additional VOCs
  selectedAdditionalVOCs: any[] = [];

  // Store selected UOC ID
  selectedUOCId: number | null = null;

  // Store selected VOC ID
  selectedVOCId: number | null = null;


  // Control participant modal visibility
  showParticipantModal = false;

  // Store participant given name
  participantGivenName = '';

  // Store participant family name
  participantFamilyName = '';

  // Store participant email
  participantEmail = '';

  // Store participant phone
  participantPhone = '';

  // Store participant form error
  participantFormError = '';

  // Track participant submission
  isSubmittingParticipant = false;


  // Store booking success message
  bookingSuccessMessage = '';

  // Store booking error message
  bookingErrorMessage = '';

  // Store calendar availability value
  isCourseAvailableInCalendar: boolean = false;


  constructor(
    private adminService: AdminService,
    private orderService: OrderService,
    private route: ActivatedRoute,
    private router: Router
  ) {
  }


  ngOnInit(): void {
 
    // Read route query parameters
    this.route.queryParamMap.subscribe(params => {

      // Store course type from route
      const routeType =
        String(
          params.get('type') || 'UOC'
        ).toUpperCase();

      // Set selected course type
      this.selectedCourseType =
        routeType === 'VOC'
          ? 'VOC'
          : 'UOC';


      // Store calendar availability from route
      const calendarAvailability =
        params.get('isCourseAvailableInCalendar') === 'true';

      // Set calendar availability value
      this.isCourseAvailableInCalendar =
        calendarAvailability;


      // Store course ID from route
      const courseId =
        Number(
          params.get('courseId')
        );

      // Set selected course ID
      this.selectedCourseId =
        courseId > 0
          ? courseId
          : null;


      // Store VOC ID from route
      const vocId =
        Number(
          params.get('vocId')
        );

      // Set selected VOC ID
      this.selectedVOCIdFromRoute =
        vocId > 0
          ? vocId
          : null;


      // Use course ID when VOC ID is unavailable
      if (!this.selectedVOCIdFromRoute) {

        const courseId =
          Number(
            params.get('courseId')
          );

        this.selectedVOCIdFromRoute =
          courseId > 0
            ? courseId
            : null;
      }


      // Check whether course should open from route
      this.openCourseFromRoute =
        String(
          params.get('openCourse')
        ).toLowerCase() === 'true';


      // Mark route parameters as loaded
      this.routeParamsLoaded = true;

      // Initialise selected course
      this.tryInitialiseSelectedCourse();
    });


    // Load UOC courses
    this.loadUOCDefCourses();

    // Load VOC courses
    this.loadVOCCourses();
  }

  // Load default UOC courses
  loadUOCDefCourses(): void {

    this.adminService
      .getXCourseList('GET_ALL')
      .subscribe({

        next: (response: any[]) => {

          // Store UOC course list
          this.allUOCCourses =
            response || [];

          // Mark UOC courses as loaded
          this.uocCoursesLoaded = true;

          // Initialise selected course
          this.tryInitialiseSelectedCourse();
        },

        error: (error) => {

          // Log UOC loading error
          console.error(
            'UOC Error:',
            error
          );

          // Reset UOC course list
          this.allUOCCourses = [];

          // Mark UOC courses as loaded
          this.uocCoursesLoaded = true;

          // Initialise selected course
          this.tryInitialiseSelectedCourse();
        }
      });
  }


  // Handle component destroy
  ngOnDestroy(): void {
    if (this.dropdownSearchTimer !== null) {
      clearTimeout(this.dropdownSearchTimer);
      this.dropdownSearchTimer = null;
    }
    this.detachCourseDropdownScroll();
    this.dropdownRequestVersion++;
  }

  // ============================================================
  // INITIALISE PAGINATED UOC DROPDOWN
  // ============================================================
  loadDropdownUOCCourses(): void {
    this.dropdownRequestVersion++;
    this.loadedDropdownPages.clear();
    this.dropdownUOCCourses = [];
    this.dropdownTotalRecords = 0;
    this.dropdownHasLoaded = false;
    this.dropdownLoading = false;
    this.loadDropdownPage(1);
  }

  // Request one SQL page (no null placeholders or virtual scroller).
  private loadDropdownPage(page: number): void {
    if (page < 1 || this.dropdownLoading || this.loadedDropdownPages.has(page)) {
      return;
    }
    if (
      this.dropdownHasLoaded &&
      this.dropdownUOCCourses.length >= this.dropdownTotalRecords
    ) {
      return;
    }

    const version = this.dropdownRequestVersion;
    const filter: XCourseFilter = {
      action: 'GET_ALL_DROPDOWN_PAGED',
      type: '',
      search: this.dropdownSearchText.trim(),
      searchKey: '',
      isActive: true,
      stateCode: '',
      currentPage: page,
      pageSize: this.dropdownPageSize
    };

    this.dropdownLoading = true;
    this.adminService.getXCourseDropdownPage(filter).subscribe({
      next: (response: any) => {
        if (version !== this.dropdownRequestVersion) {
          return;
        }

        const records = Array.isArray(response?.records)
          ? response.records.filter((course: any) => course && Number(course.id) > 0)
          : [];

        this.dropdownTotalRecords = Number(response?.totalRecords || 0);
        this.dropdownHasLoaded = true;

        const existingIds = new Set(
          this.dropdownUOCCourses.map(course => Number(course.id))
        );
        this.dropdownUOCCourses = [
          ...this.dropdownUOCCourses,
          ...records.filter((course: any) => !existingIds.has(Number(course.id)))
        ];

        this.loadedDropdownPages.add(page);
        this.dropdownLoading = false;
      },
      error: (error: any) => {
        if (version !== this.dropdownRequestVersion) {
          return;
        }
        console.error('Unable to load dropdown UOC courses:', error);
        this.dropdownLoading = false;
      }
    });
  }

  // When the PrimeNG dropdown opens, attach to its single list scrollbar.
  onCourseDropdownOpened(): void {
    if (!this.dropdownHasLoaded && !this.dropdownLoading) {
      this.loadDropdownPage(1);
    }
    setTimeout(() => this.attachCourseDropdownScroll(), 0);
  }

  onCourseDropdownHidden(): void {
    this.detachCourseDropdownScroll();
  }

  private attachCourseDropdownScroll(): void {
    const el = document.querySelector(
      '.ecb-single-enquire-uoc-dropdown-panel .p-dropdown-items-wrapper'
    ) as HTMLElement | null;

    if (el === this.dropdownScrollElement) {
      return;
    }
    this.detachCourseDropdownScroll();
    if (el) {
      this.dropdownScrollElement = el;
      el.addEventListener('scroll', this.dropdownScrollHandler, { passive: true });
    }
  }

  private detachCourseDropdownScroll(): void {
    if (this.dropdownScrollElement) {
      this.dropdownScrollElement.removeEventListener('scroll', this.dropdownScrollHandler);
      this.dropdownScrollElement = null;
    }
  }

  // Load the next page of 50; footer button is fallback if there is no scrollbar.
  onCourseDropdownScroll(): void {
    if (
      this.dropdownLoading ||
      !this.dropdownHasLoaded ||
      this.dropdownUOCCourses.length >= this.dropdownTotalRecords
    ) {
      return;
    }
    this.loadDropdownPage(this.loadedDropdownPages.size + 1);
  }

  // Search ALL matching SQL records, rather than filtering just the loaded page.
  onDropdownSearch(value: string): void {
    this.dropdownSearchText = value;
    if (this.dropdownSearchTimer !== null) {
      clearTimeout(this.dropdownSearchTimer);
    }
    this.dropdownSearchTimer = setTimeout(() => {
      this.dropdownSearchTimer = null;
      this.dropdownSearchText = value.trim();
      this.loadDropdownUOCCourses();
    }, 350);
  }


  // Load UOC courses
  loadUOCCourses(): void {

    // Set UOC load type
    let type = 'GET_ALL';

    /*
    if (this.isNotHighRiskCourse) {
      type = 'GET_UOC_ONLY';
    }
    */

    this.adminService
      .getXCourseList(type)
      .subscribe({

        next: (response: any[]) => {

          // Store UOC response
          const courses =
            response || [];

          // Set UOC course list
          this.allUOCCourses =
            courses;
        },

        error: (error) => {

          // Log UOC loading error
          console.error(
            'UOC Error:',
            error
          );
        }
      });
  }


  // Load VOC courses
  loadVOCCourses(): void {

    this.adminService
      .getXVOCList('GET_ALL')
      .subscribe({

        next: (response: any[]) => {

          // Store VOC course list
          this.allVOCCourses =
            response || [];

          /*
          if (this.isNotHighRiskCourse) {
  
            this.allVOCCourses =
              this.allVOCCourses.filter(course =>
                course?.vocCode
                  ?.toUpperCase()
                  .startsWith('WP')
              );
  
          } else {
          */

          // Keep all VOC courses
          this.allVOCCourses =
            this.allVOCCourses;

          // }

          // Mark VOC courses as loaded
          this.vocCoursesLoaded = true;

          // Initialise selected course
          this.tryInitialiseSelectedCourse();
        },

        error: (error) => {

          // Log VOC loading error
          console.error(
            'VOC Error:',
            error
          );

          // Reset VOC course list
          this.allVOCCourses = [];

          // Mark VOC courses as loaded
          this.vocCoursesLoaded = true;

          // Initialise selected course
          this.tryInitialiseSelectedCourse();
        }
      });
  }

  // Initialise selected course
  private tryInitialiseSelectedCourse(): void {

    // Check required data is loaded
    if (
      !this.routeParamsLoaded ||
      !this.uocCoursesLoaded ||
      !this.vocCoursesLoaded ||
      this.initialCourseApplied
    ) {
      return;
    }


    // Mark initial course as applied
    this.initialCourseApplied = true;

    // Reset booking messages
    this.bookingErrorMessage = '';
    this.bookingSuccessMessage = '';

    // Reset selected courses
    this.selectedAdditionalUOCs = [];
    this.selectedAdditionalVOCs = [];

    // Reset selected course IDs
    this.selectedUOCId = null;
    this.selectedVOCId = null;


    // ==========================================================
    // UOC SELECTED FROM DETAILS PAGE
    // ==========================================================
 
     
if (this.selectedCourseType === 'UOC') {

  // Validate selected course ID
  const courseId = Number(this.selectedCourseId);

  if (!courseId) {
    this.selectedCourseDetails = null;
    this.showCourseModal = false;
    this.bookingErrorMessage =
      'Please select a valid UOC course.';
    return;
  }

  // Reset previous course details
  this.selectedCourseDetails = null;
  this.selectedParentUOCDetails = null;
  this.showCourseModal = false;
  this.bookingErrorMessage = '';

  // Load full UOC details from API
  this.adminService.GetXCourseFullDetails(courseId)
    .subscribe({

      next: (selectedUOC: any) => {

        // Check selected UOC exists
        if (!selectedUOC || !selectedUOC.id) {

          this.selectedCourseDetails = null;
          this.showCourseModal = false;

          this.bookingErrorMessage =
            'The selected UOC course could not be loaded.';

          return;
        }

        // Store UOC display details
        this.selectedCourseDetails =
          this.createUOCDisplayDetails(selectedUOC);

        // Reload VOC choices
        this.loadVOCCourses();

        // Store primary requested UOC
        this.selectedAdditionalUOCs = [
          {
            ...selectedUOC,
            isPrimaryRequestedCourse: true
          }
        ];

        // Reset course selections
        this.selectedUOCId = null;
        this.selectedVOCId = null;

        // Clear parent UOC details
        this.selectedParentUOCDetails = null;

        // Control course modal visibility
        this.showCourseModal =
          this.openCourseFromRoute ||
          !!this.selectedCourseDetails;

      },

      error: (error) => {

        console.error(
          'Error loading UOC course details:',
          error
        );

        this.selectedCourseDetails = null;
        this.showCourseModal = false;

        this.bookingErrorMessage =
          'Unable to load the selected UOC course. Please try again.';

      }

    });

  return;
}


    // ==========================================================
    // VOC SELECTED FROM DETAILS PAGE
    // ==========================================================

    // Find selected VOC
    const selectedVOC =
      this.allVOCCourses.find(
        voc =>
          Number(voc.id) ===
          Number(this.selectedVOCIdFromRoute)
      );


    // Check selected VOC exists
    if (!selectedVOC) {

      this.selectedCourseDetails = null;
      this.showCourseModal = false;

      this.bookingErrorMessage =
        'The selected VOC course could not be loaded.';

      return;
    }


    // Store parent UOC ID
    const parentUOCId =
      Number(
        this.selectedCourseId ||
        selectedVOC.fkParentCourseId ||
        0
      );


    // Store parent UOC details
    this.selectedParentUOCDetails =
      this.allUOCCourses.find(
        course =>
          Number(course.id) ===
          parentUOCId
      ) || null;


    // Store VOC display details
    this.selectedCourseDetails =
      this.createVOCDisplayDetails(
        selectedVOC,
        parentUOCId
      );


    // Store primary requested VOC
    this.selectedAdditionalVOCs = [
      {
        ...selectedVOC,
        isPrimaryRequestedCourse: true
      }
    ];


    // Reset course selections
    this.selectedVOCId = null;
    this.selectedUOCId = null;


    // Control course modal visibility
    this.showCourseModal =
      this.openCourseFromRoute ||
      !!this.selectedCourseDetails;
  }


  // ============================================================
  // NORMALISE UOC DETAILS FOR THE EXISTING MODAL
  // ============================================================

  // Create UOC display details
  private createUOCDisplayDetails(
    course: any
  ): any {

    return {
      id: null,
      courseId: Number(course.id),
      courseType: 'UOC',
      courseCode: course.courseCode || '',
      courseName: course.courseTitle || '',
      description: course.description || '',
      totalAmount: Number(course.totalAmount || 0),
      currencyCode: course.currencyCode || 'AUD',
      documentTypes: course.documentTypes || '',
      parentCourseId: null,
      registrationStartDate: null,
      registrationEndDate: null,
      startDate: null,
      endDate: null,
      location: '',
      capacity: null,
      availableSeats: null,
      vettrackOccuranceId: null,
      vettrackJobNumber: null
    };
  }


  // ============================================================
  // NORMALISE VOC DETAILS FOR THE EXISTING MODAL
  // ============================================================

  // Create VOC display details
  private createVOCDisplayDetails(
    voc: any,
    parentUOCId: number
  ): any {

    return {
      id: null,
      courseId: Number(voc.id),
      courseType: 'VOC',
      courseCode: voc.vocCode || '',
      courseName: voc.vocName || '',
      description: voc.description || '',
      totalAmount: Number(voc.totalAmount || 0),
      currencyCode: voc.currencyCode || 'AUD',
      documentTypes: voc.documentTypes || '',

      parentCourseId:
        parentUOCId > 0
          ? parentUOCId
          : null,

      fkParentCourseId:
        voc.fkParentCourseId ||
        parentUOCId ||
        null,

      registrationStartDate: null,
      registrationEndDate: null,
      startDate: null,
      endDate: null,
      location: '',
      capacity: null,
      availableSeats: null,
      vettrackOccuranceId: null,
      vettrackJobNumber: null
    };
  }


  // ============================================================
  // DISPLAY HELPERS
  // ============================================================

  // Get selected course type label
  get selectedCourseTypeLabel(): string {

    return this.selectedCourseType === 'VOC'
      ? 'Verification of Competency (VOC)'
      : 'Unit of Competency (UOC)';
  }


  // Get selected parent UOC text
  get selectedParentUOCText(): string {

    if (!this.selectedParentUOCDetails) {
      return '-';
    }

    return `${this.selectedParentUOCDetails.courseCode} - ${this.selectedParentUOCDetails.courseTitle}`;
  }


  // ============================================================
  // ADD UOC
  // ============================================================

  // Add selected UOC
  addSelectedUOC(): void {

    // Check selected UOC ID
    if (this.selectedUOCId === null) {
      return;
    }


    // Find selected UOC
    const selectedCourse =
      this.allUOCCourses.find(
        course =>
          Number(course.id) ===
          Number(this.selectedUOCId)
      );


    // Check selected UOC exists
    if (!selectedCourse) {
      this.selectedUOCId = null;
      return;
    }


    // Check duplicate UOC
    const alreadySelected =
      this.selectedAdditionalUOCs.some(
        course =>
          Number(course.id) ===
          Number(selectedCourse.id)
      );


    // Add selected UOC
    if (!alreadySelected) {

      this.selectedAdditionalUOCs = [
        ...this.selectedAdditionalUOCs,
        selectedCourse
      ];
    }


    // Reset selected UOC ID
    this.selectedUOCId = null;
  }


  // Get available UOC courses for the paginated dropdown
  get availableUOCCourses(): any[] {

    return this.dropdownUOCCourses.filter(course => {

      // Check UOC is already selected
      const alreadySelected =
        this.selectedAdditionalUOCs.some(
          selected =>
            Number(selected.id) ===
            Number(course.id)
        );

      return !alreadySelected;
    });
  }


  // ============================================================
  // REMOVE UOC
  // ============================================================

  // Remove selected UOC
  removeSelectedUOC(
    courseId: number
  ): void {

    // Find selected UOC
    const course =
      this.selectedAdditionalUOCs.find(
        item =>
          Number(item.id) ===
          Number(courseId)
      );


    // Prevent primary UOC removal
    if (
      course?.isPrimaryRequestedCourse ||
      course?.isPrimaryCalendarCourse
    ) {
      return;
    }


    // Remove selected UOC
    this.selectedAdditionalUOCs =
      this.selectedAdditionalUOCs.filter(
        item =>
          Number(item.id) !==
          Number(courseId)
      );
  }


  // ============================================================
  // ADD VOC
  // ============================================================

  // Add selected VOC
  addSelectedVOC(): void {

    // Check selected VOC ID
    if (this.selectedVOCId === null) {
      return;
    }


    // Find selected VOC
    const selectedVOC =
      this.allVOCCourses.find(
        voc =>
          Number(voc.id) ===
          Number(this.selectedVOCId)
      );


    // Check selected VOC exists
    if (!selectedVOC) {
      this.selectedVOCId = null;
      return;
    }


    // Check duplicate VOC
    const alreadySelected =
      this.selectedAdditionalVOCs.some(
        voc =>
          Number(voc.id) ===
          Number(selectedVOC.id)
      );


    // Add selected VOC
    if (!alreadySelected) {

      this.selectedAdditionalVOCs = [
        ...this.selectedAdditionalVOCs,
        selectedVOC
      ];
    }


    // Reset selected VOC ID
    this.selectedVOCId = null;
  }


  // Get available VOC courses
  get availableVOCCourses(): any[] {

    return this.allVOCCourses.filter(voc => {

      // Check VOC is already selected
      const alreadySelected =
        this.selectedAdditionalVOCs.some(
          selected =>
            Number(selected.id) ===
            Number(voc.id)
        );

      return !alreadySelected;
    });
  }


  // ============================================================
  // REMOVE VOC
  // ============================================================

  // Remove selected VOC
  removeSelectedVOC(
    vocId: number
  ): void {

    // Find selected VOC
    const voc =
      this.selectedAdditionalVOCs.find(
        item =>
          Number(item.id) ===
          Number(vocId)
      );


    // Prevent primary VOC removal
    if (voc?.isPrimaryRequestedCourse) {
      return;
    }


    // Remove selected VOC
    this.selectedAdditionalVOCs =
      this.selectedAdditionalVOCs.filter(
        item =>
          Number(item.id) !==
          Number(vocId)
      );
  }


  // ============================================================
  // ADDITIONAL COURSE HELPERS
  // ============================================================

  // Get additional UOC courses
  get additionalUOCsOnly(): any[] {

    return this.selectedAdditionalUOCs.filter(
      course =>
        !course.isPrimaryRequestedCourse &&
        !course.isPrimaryCalendarCourse
    );
  }


  // Get additional VOC courses
  get additionalVOCsOnly(): any[] {

    return this.selectedAdditionalVOCs.filter(
      voc =>
        !voc.isPrimaryRequestedCourse
    );
  }


  // Get additional UOC count
  get additionalUOCCount(): number {
    return this.additionalUOCsOnly.length;
  }


  // Get additional VOC count
  get additionalVOCCount(): number {
    return this.additionalVOCsOnly.length;
  }


  // Check additional courses
  get hasAdditionalCourses(): boolean {

    return (
      this.additionalUOCCount > 0 ||
      this.additionalVOCCount > 0
    );
  }


  // ============================================================
  // PRIMARY ACTION
  // ============================================================

  // Get primary action text
  get primaryActionText(): string {

    // Check direct enquiry
    if (this.isDirectEnquiry) {
      return 'Enquire Now';
    }


    // Store course amount
    const amount =
      Number(
        this.selectedCourseDetails?.totalAmount ?? 0
      );


    // Check enquiry action
    if (
      this.hasAdditionalCourses ||
      amount <= 0
    ) {
      return 'Enquire Now';
    }

    return 'Pay Now';
  }


  // ============================================================
  // PARTICIPANT ACTION
  // ============================================================

  // Get participant action text
  get participantActionText(): string {

    // Check direct enquiry
    if (this.isDirectEnquiry) {
      return 'Send Enquiry';
    }


    // Store course amount
    const amount =
      Number(
        this.selectedCourseDetails?.totalAmount ?? 0
      );


    // Check enquiry action
    if (
      this.hasAdditionalCourses ||
      amount <= 0
    ) {
      return 'Send Enquiry';
    }

    return `Pay A$${amount.toFixed(2)}`;
  }


  // ============================================================
  // TOTAL COURSE COUNT
  // ============================================================

  // Get total selected courses
  get totalSelectedCourses(): number {

    return (
      1 +
      this.additionalUOCCount +
      this.additionalVOCCount
    );
  }


  // ============================================================
  // CLOSE COURSE DETAILS
  // ============================================================

  // Close course details
  closeCourseDetails(): void {

    // Hide course modal
    this.showCourseModal = false;

    // Return to previous page
    if (window.history.length > 1) {
      window.history.back();
    }
  }


  // ============================================================
  // OPEN PARTICIPANT DETAILS
  // ============================================================

  // Open participant details
  openParticipantDetails(): void {

    // Check selected course
    if (!this.selectedCourseDetails) {
      return;
    }


    // Reset participant details
    this.participantGivenName = '';
    this.participantFamilyName = '';
    this.participantEmail = '';
    this.participantFormError = '';


    // Open participant modal
    this.showCourseModal = false;
    this.showParticipantModal = true;
  }


  // ============================================================
  // BACK TO COURSE DETAILS
  // ============================================================

  // Return to course details
  backToCourseDetails(): void {

    this.showParticipantModal = false;
    this.showCourseModal = true;
  }


  // ============================================================
  // CLOSE PARTICIPANT
  // ============================================================

  // Close participant modal
  closeParticipantModal(): void {

    // Reset participant modal
    this.showParticipantModal = false;
    this.participantFormError = '';

    // Show course modal
    this.showCourseModal = true;
  }


  // ============================================================
  // PARTICIPANT SUBMIT
  // ============================================================

  // Submit participant details
  submitParticipantDetails(): void {

    // Reset participant form error
    this.participantFormError = '';


    // Validate given name
    if (!this.participantGivenName.trim()) {

      this.participantFormError =
        'Please enter the participant given name.';

      return;
    }


    // Validate email
    if (!this.participantEmail.trim()) {

      this.participantFormError =
        'Please enter the participant email address.';

      return;
    }


    // Validate contact number
    if (!this.participantPhone.trim()) {

      this.participantFormError =
        'Please enter the participant contact number.';

      return;
    }


    // Check direct enquiry
    if (this.isDirectEnquiry) {

      this.sendBookingEnquiry();
      return;
    }


    // Check enquiry condition
    if (
      this.hasAdditionalCourses ||
      Number(this.selectedCourseDetails?.totalAmount || 0) <= 0
    ) {

      this.sendBookingEnquiry();
      return;
    }


    // Process payment
    this.processSingleCoursePayment();
  }


  // ============================================================
  // CREATE BOOKING JSON
  // ============================================================

  // Create booking JSON
  createBookingJson(): any {


     

    // Store selected course amount
    const t = this.selectedCourseDetails?.totalAmount;

    // Store calendar availability
    const l = this.isCourseAvailableInCalendar;


    // Store booking type
    const ii =
      this.hasAdditionalCourses
        ? 'MULTI_COURSE_ENQUIRY'
        : (
          Number(this.selectedCourseDetails?.totalAmount ?? 0) > 0 &&
          this.isCourseAvailableInCalendar
        )
          ? 'SINGLE_UOC_PAYMENT'
          : 'SINGLE_UOC_ENQUIRY';


    return {

      /* bookingType:
       this.hasAdditionalCourses
         ? 'MULTI_COURSE_ENQUIRY'
         : Number(this.selectedCourseDetails?.totalAmount ?? 0) > 0
           ? 'SINGLE_UOC_PAYMENT'
           : 'SINGLE_UOC_ENQUIRY', */

      // Store booking type
      bookingType:
        this.hasAdditionalCourses
          ? 'MULTI_COURSE_ENQUIRY'
          : (
            Number(this.selectedCourseDetails?.totalAmount ?? 0) > 0 &&
            this.isCourseAvailableInCalendar
          )
            ? 'SINGLE_UOC_PAYMENT'
            : 'SINGLE_UOC_ENQUIRY',

      // Store course type
      courseType:
        this.selectedCourseType,


      // Store participant details
      participant: {
        contactNumber: this.participantPhone,

        givenName:
          this.participantGivenName,

        familyName:
          this.participantFamilyName,

        email:
          this.participantEmail
      },


      // Store primary course details
      primaryCourse: {

        courseId:
          this.selectedCourseDetails
            ?.courseId,

        courseType:
          this.selectedCourseType,

        courseCode:
          this.selectedCourseDetails
            ?.courseCode,

        courseTitle:
          this.selectedCourseDetails
            ?.courseName,

        description:
          this.selectedCourseDetails
            ?.description,

        totalAmount:
          this.selectedCourseDetails
            ?.totalAmount,

        currencyCode:
          this.selectedCourseDetails
            ?.currencyCode,

        documentTypes:
          this.selectedCourseDetails
            ?.documentTypes,

        parentCourseId:
          this.selectedCourseDetails
            ?.parentCourseId ||
          this.selectedCourseDetails
            ?.fkParentCourseId ||
          null,

        // Store occurrence details
        occurrence:
          this.isDirectEnquiry
            ? null
            : {

              occurrenceId:
                this.selectedCourseDetails
                  ?.id,

              startDate:
                this.selectedCourseDetails
                  ?.startDate,

              endDate:
                this.selectedCourseDetails
                  ?.endDate,

              registrationStartDate:
                this.selectedCourseDetails
                  ?.registrationStartDate,

              registrationEndDate:
                this.selectedCourseDetails
                  ?.registrationEndDate,

              location:
                this.selectedCourseDetails
                  ?.location,

              capacity:
                this.selectedCourseDetails
                  ?.capacity,

              availableSeats:
                this.selectedCourseDetails
                  ?.availableSeats,

              vettrackOccuranceId:
                this.selectedCourseDetails
                  ?.vettrackOccuranceId,

              vettrackJobNumber:
                this.selectedCourseDetails
                  ?.vettrackJobNumber
            }
      },


      // Store additional UOC courses
      additionalUOCCourses:
        this.additionalUOCsOnly.map(
          course => ({

            courseId:
              course.id,

            courseCode:
              course.courseCode,

            courseTitle:
              course.courseTitle,

            description:
              course.description,

            totalAmount:
              course.totalAmount,

            currencyCode:
              course.currencyCode,

            documentTypes:
              course.documentTypes
          })
        ),


      /*
       * Keep the primary requested VOC in this list too.
       * This allows the registration flow to receive the VOC selection.
       */

      // Store VOC courses
      vocCourses:
        this.selectedAdditionalVOCs.map(
          voc => ({

            vocId:
              voc.id,

            vocCode:
              voc.vocCode,

            vocName:
              voc.vocName,

            description:
              voc.description,

            documentTypes:
              voc.documentTypes,

            fkParentCourseId:
              voc.fkParentCourseId,

            isPrimaryCourse:
              !!voc.isPrimaryRequestedCourse
          })
        ),


      // Store total selected courses
      totalCourses:
        this.totalSelectedCourses,

      // Store created date
      createdDate:
        new Date().toISOString()
    };
  }


  // Close enquiry success popup
  closeEnquirySuccessPopup(): void {

    // Hide success popup
    this.showEnquirySuccessPopup = false;

    // Navigate to home page
    this.router.navigate(['/']);
  }


  // Control enquiry success popup visibility
  showEnquirySuccessPopup: boolean = false;


  // ============================================================
  // SEND ENQUIRY
  // ============================================================

  // Send booking enquiry
  sendBookingEnquiry(): void {

    // Create booking JSON
    const bookingJson =
      this.createBookingJson();


    // Create enquiry model
    const enquiryModel = {

      id: 0,

      body:
        JSON.stringify(
          bookingJson
        ),

      sentBy:
        this.participantEmail,

      reviewedBy:
        null,

      reviewedDate:
        null,

      isApproved:
        false,

      isActive:
        true,

      clientType:
        'GENERAL'
    };


    // Start participant submission
    this.isSubmittingParticipant = true;


    // Submit enquiry
    this.adminService
      .setXEnquiry(
        enquiryModel,
        'INSERT'
      )
      .subscribe({

        next: (response) => {

       
 
          // Stop participant submission
          this.isSubmittingParticipant = false;

          // Update modal visibility
          this.showParticipantModal = false;
          this.showCourseModal = true;

          // Store success message
          this.bookingSuccessMessage =
            'Enquiry submitted successfully.';

          // Show success popup
          this.showEnquirySuccessPopup = true;
        },


        error: (error) => {

          console.error(
            'Enquiry Error:',
            error
          );


          // Stop participant submission
          this.isSubmittingParticipant = false;

          // Store enquiry error
          this.participantFormError =
            'Unable to send the enquiry. Please try again.';
        }
      });
  }


  // ============================================================
  // SINGLE COURSE PAYMENT
  // ============================================================

  // Process single course payment
  processSingleCoursePayment(): void {

    // Validate payment details
    if (
      !this.selectedCourseDetails ||
      !this.participantGivenName?.trim() ||
      !this.participantEmail?.trim()
    ) {
      return;
    }


    // Create payment request
    const request: ShortCourseOrderModel = {

      id: 0,

      vetTrackId:
        Number(
          this.selectedCourseDetails.courseId
        ),

      shortCourseTitle:
        this.selectedCourseDetails.courseName,

      studentGivenName:
        this.participantGivenName.trim(),

      studentSingleName:
        this.participantFamilyName?.trim() || '',

      studentEmail:
        this.participantEmail.trim(),

      totalAmount:
        Number(
          this.selectedCourseDetails.totalAmount || 0
        ),

      currencyCode:
        this.selectedCourseDetails.currencyCode || 'AUD',

      stripeSessionId:
        null,

      stripePaymentIntentId:
        null,

      paymentStatus:
        'Pending',

      createdDate:
        null,

      noOfSeatedLeft:
        Number(
          this.selectedCourseDetails.availableSeats || 0
        ),

      type:
        'REGULAR',

      jobNo:
        this.selectedCourseDetails.vettrackJobNumber || null,

      startDate:
        this.selectedCourseDetails.startDate,

      endDate:
        this.selectedCourseDetails.endDate,

      duration:
        ''
    };


    // Start participant submission
    this.isSubmittingParticipant = true;


    // Create Stripe checkout
    this.orderService
      .createStripeShortCheckout(request)
      .subscribe({

        next: response => {

          // Stop participant submission
          this.isSubmittingParticipant = false;


          // Check checkout URL
          if (response?.checkoutUrl) {

            // Store pending booking
            sessionStorage.setItem(
              'ecb_pending_booking',
              JSON.stringify(
                {

                  OrderId:
                    response.orderId,

                  studentGivenName:
                    this.participantGivenName,

                  studentFamilyName:
                    this.participantFamilyName,

                  studentEmail:
                    this.participantEmail,

                  totalAmount:
                    Number(
                      this.selectedCourseDetails.totalAmount || 0
                    ),

                  currencyCode:
                    this.selectedCourseDetails.currencyCode || 'AUD',

                  courseId:
                    this.selectedCourseDetails.courseId,

                  courseName:
                    this.selectedCourseDetails.courseName,

                  courseCode:
                    this.selectedCourseDetails.courseCode,

                  occurrenceId:
                    this.selectedCourseDetails.id,

                  startDate:
                    this.selectedCourseDetails.startDate,

                  endDate:
                    this.selectedCourseDetails.endDate,

                  location:
                    this.selectedCourseDetails.location,

                  availableSeats:
                    this.selectedCourseDetails.availableSeats,

                  duration:
                    '',

                  vettrackOccuranceId:
                    this.selectedCourseDetails.vettrackOccuranceId,

                  vettrackJobNumber:
                    this.selectedCourseDetails.vettrackJobNumber
                }
              )
            );


            // Redirect to Stripe checkout
            window.location.href =
              response.checkoutUrl;

          } else {

            console.error(
              'Stripe checkout URL was not returned.'
            );

            // Store checkout error
            this.participantFormError =
              'Unable to start the payment. Please try again.';
          }
        },


        error: error => {

          // Stop participant submission
          this.isSubmittingParticipant = false;

          console.error(
            'Stripe checkout failed:',
            error
          );

          // Store payment error
          this.participantFormError =
            'Payment could not be started. Please try again.';
        }
      });
  }
}